import { createAsyncThunk } from '@reduxjs/toolkit';
import { locale } from 'src/i18n';
import { CRM_UPDATE_STATUS } from 'src/modules/constants';
import { RootState } from 'src/store';
import axios, { isWorkflowApiRequest } from 'src/workspace/axios';
import { moduleConfigs } from './constants';
import { mapCrmUpdateStatusColumns } from './queries';
import {
  getGenericListCurrentPage,
  getGenericListFilters,
  getGenericListPageSize,
} from './selector';
import {
  clearNotification,
  setError,
  setGenericListColumns,
  setGenericListMetaData,
  setGenericListRows,
  setLoading,
} from './slice';

// Generic API call helper
const fetchApiData = async (
  url: string,
  payload: Record<any, any>,
  dispatch: any,
  errorKey: string,
  module?: string,
) => {
  try {
    dispatch(setLoading({ loading: true }));
    let response;

    switch (module) {
      default:
        response = await axios.post(url, payload);
        break;
    }

    return response.data;
  } catch (error) {
    if (isWorkflowApiRequest(error)) {
      // eslint-disable-next-line no-console
      console.log(error);
    } else {
      dispatch(setError({ message: locale(errorKey) }));
    }
    return null;
  } finally {
    dispatch(setLoading({ loading: false }));
  }
};

const getModuleColumns = async (
  module: string | undefined,
  config: (typeof moduleConfigs)[string],
) => {
  switch (module) {
    case CRM_UPDATE_STATUS: {
      if (!config.configUrl) {
        return [];
      }

      try {
        const response = await axios.get(config.configUrl);
        return mapCrmUpdateStatusColumns(
          response.data?.payload?.columnConfigs || [],
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
        return [];
      }
    }

    default:
      return config.columns;
  }
};

// Extract metadata from response
const getMetadata = (
  data: any,
  pageSize: number,
  currentPage: number,
  customGetter?: (
    data: any,
    pageSize: number,
    currentPage: number,
  ) => {
    totalCount?: number;
    maxPages?: number;
    pageSize?: number;
    currentPage?: number;
  },
) => {
  if (customGetter) {
    return customGetter(data, pageSize, currentPage);
  }
  return {
    totalCount: data?.totalElements,
    maxPages: data?.totalPages,
    pageSize: data?.pageSize || pageSize,
    currentPage: (data?.currentPage ?? currentPage - 1) + 1,
  };
};

// Process response data
const processResponse = (
  data: any,
  dispatch: any,
  pageSize: number,
  currentPage: number,
  config: {
    rowsKey: string | string[];
    columns?: any[];
    getMetadata?: (
      data: any,
      pageSize: number,
      currentPage: number,
    ) => {
      totalCount?: number;
      maxPages?: number;
      pageSize?: number;
      currentPage?: number;
    };
    getColumns?: (data: any) => any[] | undefined;
  },
) => {
  const rows = Array.isArray(config.rowsKey)
    ? config.rowsKey.reduce((acc, key) => acc || data?.[key], null) || []
    : data?.[config.rowsKey] || [];

  const metadata = getMetadata(data, pageSize, currentPage, config.getMetadata);
  const columns = config.columns || config.getColumns?.(data) || data?.columns;

  if (columns) {
    dispatch(setGenericListColumns(columns));
  }

  dispatch(setGenericListRows(rows));
  dispatch(
    setGenericListMetaData({
      totalCount: metadata.totalCount,
      maxPages: metadata.maxPages,
      pageSize: metadata.pageSize || pageSize,
      currentPage: metadata.currentPage || currentPage,
      loading: false,
    }),
  );
};

export const fetchModuleTableData = createAsyncThunk<
  void,
  { module?: string; filters?: Record<string, any> }
>(
  'fetchModuleTableData',
  async ({ module, filters: filtersOverride }, { dispatch, getState }) => {
    const state = getState() as RootState;
    const pageSize = getGenericListPageSize(state);
    const currentPage = getGenericListCurrentPage(state);
    const filters = filtersOverride ?? getGenericListFilters(state);

    const config = moduleConfigs[module || ''] || moduleConfigs.default;
    const columns = await getModuleColumns(module, config);

    const basePayload = {
      page_size: pageSize,
      page_number: currentPage,
      sort: '',
      sortType: '',
      web: true,
      module: module || '',
      filters: filters || {},
      from_web2: true,
    };

    const payload = config.getPayload(basePayload);

    const responseData: any = await fetchApiData(
      config.url,
      payload,
      dispatch,
      config.errorKey,
      module,
    );
    if (responseData) {
      const data = config.getData ? config.getData(responseData) : responseData;
      if (data) {
        processResponse(data, dispatch, pageSize, currentPage, {
          rowsKey: config.rowsKey,
          columns,
          getMetadata: config.getMetadata,
          getColumns: config.getColumns,
        });
      }
    }
  },
);

let lastActivatedModule: string | undefined;

const onActivate = ({ dispatch, props }) => {
  dispatch(clearNotification());
  const module = props?.module;
  lastActivatedModule = module;
  dispatch(setLoading({ loading: true }));
  dispatch(fetchModuleTableData({ module }));
};

const onUpdate = async ({ dispatch, props }) => {
  const module = props?.module;
  // Only refetch if the module actually changed (not on initial mount)
  if (module && module !== lastActivatedModule) {
    lastActivatedModule = module;
    dispatch(setLoading({ loading: true }));
    dispatch(fetchModuleTableData({ module }));
  }
};

const onDeactivate = () => {
  // Reset the tracked module when component deactivates
  lastActivatedModule = undefined;
};

export default { onActivate, onDeactivate, onUpdate };
